import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { INITIAL_STATE } from "./consts";
import { proxyClient } from "~/utils/api";
import { deobfuscate } from "~/utils/obfuscate";
import { CatalogPayload } from "../types";
import { CatalogStoreState } from "./types";

// // // //

/**
 * A Redux thunk that fetches the catalog payload from the server via tRPC
 * and deobfuscates it before returning.
 */
export const fetchCatalog = createAsyncThunk(
    "catalog/fetchCatalog",
    async () => {
        const response = await proxyClient.catalog.query.query();
        return deobfuscate(response as string) as CatalogPayload;
    },
    {
        // Prevent duplicate fetches when multiple CatalogTooltip components mount at once.
        // Without this guard, every tooltip dispatches its own fetch before any has completed,
        // producing dozens of identical batched `catalog.query` calls.
        condition: (_, { getState }) => {
            const { loading, items } = (
                getState() as { catalog: CatalogStoreState }
            ).catalog;
            return !loading && items.length === 0;
        },
    },
);

/**
 * The catalog slice of the Redux store.
 */
export const catalogSlice = createSlice({
    name: "catalog",
    initialState: INITIAL_STATE,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCatalog.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCatalog.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.items;
                state.total_asset_count = action.payload.total_asset_count;
            })
            .addCase(fetchCatalog.rejected, (state) => {
                state.loading = false;
            });
    },
});

/**
 * The reducer function for the catalog slice of the Redux store.
 */
export const reducer = catalogSlice.reducer;
